import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BookOpen, TrendingUp, TrendingDown, Minus, Clock, Brain, Rocket } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

const STATUS_ICONS = {
  winning: TrendingUp,
  losing: TrendingDown,
  watching: Minus,
};

const STATUS_COLORS = {
  winning: "border-bm-green/40 text-bm-green",
  losing: "border-bm-danger/40 text-bm-danger",
  watching: "border-bm-amber/40 text-bm-amber",
};

function LaunchLearning({ launch, idx }) {
  const Icon = STATUS_ICONS[launch.status] || Minus;
  const cls = STATUS_COLORS[launch.status] || "border-white/15 text-bm-muted";
  return (
    <div
      data-testid={`launch-learning-${launch.id}`}
      className="bm-card fade-up p-4 flex flex-col gap-3"
      style={{ animationDelay: `${Math.min(idx * 40, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`label-mono px-1.5 py-0.5 border ${cls} flex items-center gap-1`}>
              <Icon className="w-3 h-3" />{launch.status.toUpperCase()}
            </span>
            <span className="label-mono text-bm-dim">{launch.category}</span>
          </div>
          <h3 className="font-heading text-base font-bold text-white">{launch.name}</h3>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 border-t border-white/5 pt-3 text-center">
        <div><div className="label-mono text-bm-dim">REPEAT</div><div className="font-mono text-sm text-white">{launch.repeat_rate}%</div></div>
        <div><div className="label-mono text-bm-dim">SENTIMENT</div><div className="font-mono text-sm text-white">{launch.review_sentiment}</div></div>
        <div><div className="label-mono text-bm-dim">HOOK</div><div className="font-mono text-sm text-white">{launch.hook_performance}</div></div>
      </div>

      <div className="text-[10px] font-mono text-bm-dim flex items-center gap-1 border-t border-white/5 pt-2">
        <Clock className="w-3 h-3" />Launched {launch.launched_at}
      </div>
    </div>
  );
}

function RetrainRow({ rec, idx }) {
  const isWinning = rec.direction > 0;
  return (
    <div
      data-testid={`retrain-row-${rec._id || idx}`}
      className="border-l-2 pl-3 py-2 fade-up"
      style={{
        animationDelay: `${Math.min(idx * 30, 400)}ms`,
        borderLeftColor: isWinning ? "rgba(91,201,136,0.45)" : "rgba(224,72,72,0.45)",
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Brain className="w-3 h-3 text-bm-cyan shrink-0" />
        <span className="label-mono text-bm-dim">{rec.launch_name || rec.launch_id?.slice(0, 8)}</span>
        <span className={`label-mono px-1 py-0.5 border ${isWinning ? "border-bm-green/40 text-bm-green" : "border-bm-danger/40 text-bm-danger"} ml-auto`}>
          {isWinning ? "REINFORCE" : "ATTENUATE"} ×{rec.magnitude || 1}
        </span>
      </div>
      <div className="text-xs text-bm-muted leading-relaxed">
        Outcome score <span className={isWinning ? "text-bm-green" : "text-bm-danger"}>{rec.outcome_score ?? "—"}</span>
        {" · "}
        Updated <span className="text-bm-cyan">{rec.node_hits?.length || 0}</span> validation nodes
      </div>
    </div>
  );
}

export default function Learnings() {
  const [launches, setLaunches] = useState([]);
  const [retrains, setRetrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("launches"); // launches | retrains

  useEffect(() => {
    Promise.all([
      api.launches(),
      api.retrainHistoryAll(50).then((d) => d.records || []).catch(() => []),
    ]).then(([ls, rs]) => {
      setLaunches(ls || []);
      setRetrains(rs || []);
      setLoading(false);
    });
  }, []);

  const winning = launches.filter((l) => l.status === "winning");
  const losing = launches.filter((l) => l.status === "losing");
  const watching = launches.filter((l) => l.status === "watching");

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" />KNOWLEDGE · LEARNINGS LIBRARY
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          What we've learned.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-3xl">
          Every winning and losing SKU teaches the system. This view aggregates post-launch outcomes
          + retraining adjustments — the compounding-conviction loop in action.
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="learnings-summary">
        <div className="bm-card p-3">
          <div className="label-mono text-bm-dim">WINNING SKUs</div>
          <div className="font-heading text-3xl font-black text-bm-green mt-1">{winning.length}</div>
        </div>
        <div className="bm-card p-3">
          <div className="label-mono text-bm-dim">WATCHING</div>
          <div className="font-heading text-3xl font-black text-bm-amber mt-1">{watching.length}</div>
        </div>
        <div className="bm-card p-3">
          <div className="label-mono text-bm-dim">LOSING</div>
          <div className="font-heading text-3xl font-black text-bm-danger mt-1">{losing.length}</div>
        </div>
        <div className="bm-card p-3">
          <div className="label-mono text-bm-dim">MODEL UPDATES</div>
          <div className="font-heading text-3xl font-black text-bm-cyan mt-1">{retrains.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-white/10" data-testid="learnings-tabs">
        <button
          onClick={() => setTab("launches")}
          data-testid="tab-launches"
          className={`px-4 py-2 text-xs font-mono border-b-2 transition-colors flex items-center gap-2 ${
            tab === "launches" ? "border-bm-amber text-bm-amber" : "border-transparent text-bm-muted hover:text-white"
          }`}
        >
          <Rocket className="w-3.5 h-3.5" />SKU OUTCOMES · {launches.length}
        </button>
        <button
          onClick={() => setTab("retrains")}
          data-testid="tab-retrains"
          className={`px-4 py-2 text-xs font-mono border-b-2 transition-colors flex items-center gap-2 ${
            tab === "retrains" ? "border-bm-amber text-bm-amber" : "border-transparent text-bm-muted hover:text-white"
          }`}
        >
          <Brain className="w-3.5 h-3.5" />MODEL ADJUSTMENTS · {retrains.length}
        </button>
      </div>

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">AGGREGATING LEARNINGS...</div>}

      {tab === "launches" && !loading && (
        launches.length === 0 ? (
          <div className="text-center py-12 font-mono text-sm text-bm-muted">No launches tracked yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="launches-grid">
            {launches.map((l, i) => <LaunchLearning key={l.id} launch={l} idx={i} />)}
          </div>
        )
      )}

      {tab === "retrains" && !loading && (
        retrains.length === 0 ? (
          <div className="text-center py-12 font-mono text-sm text-bm-muted">
            No retraining cycles run yet. Trigger one from the Retraining endpoint to see model updates land here.
          </div>
        ) : (
          <div className="bm-card p-4 space-y-1" data-testid="retrains-list">
            {retrains.map((r, i) => <RetrainRow key={r._id || i} rec={r} idx={i} />)}
          </div>
        )
      )}
    </div>
  );
}
